import { useState } from "preact/hooks";
import type { TargetedEvent } from "preact";

interface LandingProps {
  busy: boolean;
  error: string;
  errorLink: string | null;
  onAnalyze(diff: string): void;
  onDemo(): void;
  onGithub(url: string): void;
}

export function Landing({ busy, error, errorLink, onAnalyze, onDemo, onGithub }: LandingProps) {
  const [diff, setDiff] = useState("");
  const [githubUrl, setGithubUrl] = useState("");

  const analyze = () => onAnalyze(diff);
  const handleFile = async (event: TargetedEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const value = await file.text();
    setDiff(value);
    onAnalyze(value);
  };

  return (
    <section class="landing" id="landing">
      <h1>cowdiff: show moooved code</h1>
      <p class="lede">
        Paste a unified diff. cowdiff pairs moved regions, highlights edits,
        and keeps everything in your browser.
      </p>

      <div class="import-card">
        <div class="import-panel">
          <label for="diff-input">Unified diff</label>
          <textarea
            id="diff-input"
            spellcheck={false}
            placeholder="Paste output from git diff, jj diff --git, or a .patch file…"
            value={diff}
            onInput={(event) => setDiff(event.currentTarget.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") analyze();
            }}
          />
          <div class="import-footer">
            <label class="file-button">
              <input
                id="file-input"
                type="file"
                accept=".diff,.patch,text/plain"
                onChange={handleFile}
              />
              <span>↑</span> Choose file
            </label>
            <div class="submit-group">
              <button class="text-button" id="demo-button" type="button" disabled={busy} onClick={onDemo}>
                Try the demo
              </button>
              <button class="primary-button" id="analyze-button" type="button" disabled={busy} onClick={analyze}>
                Analyze diff <span>→</span>
              </button>
            </div>
          </div>

          <div class="github-import">
            <label for="github-input">Or use a public GitHub pull request</label>
            <div class="url-row">
              <input
                id="github-input"
                type="url"
                placeholder="https://github.com/owner/repo/pull/123"
                value={githubUrl}
                onInput={(event) => setGithubUrl(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onGithub(githubUrl);
                }}
              />
              <button
                class="primary-button"
                id="github-button"
                type="button"
                disabled={busy}
                onClick={() => onGithub(githubUrl)}
              >
                Fetch &amp; analyze <span>→</span>
              </button>
            </div>
            <p class="hint">Private pull requests are not supported.</p>
          </div>
        </div>
        <div class={`error-message${error ? " visible" : ""}`} id="error-message" role="alert">
          {error}
          {errorLink && (
            <a href={errorLink} target="_blank" rel="noreferrer">Open full .diff ↗</a>
          )}
        </div>
      </div>
    </section>
  );
}
